import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Building2,
  Zap,
  Droplets,
  Wind,
  Users,
  CheckCircle,
  ArrowRight,
  Shield,
  Clock,
  Gauge,
  MapPin,
  Thermometer,
  Bell,
  BarChart3,
  Settings,
  Wifi,
  Box,
  Car,
  Briefcase,
  Hotel,
  Home,
  Landmark,
  ShoppingBag,
  Factory,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Leaf,
  AlertTriangle,
  Key,
  Sparkles,
  Package,
  Target,
  TrendingDown,
  ExternalLink,
  Network,
  Server,
  Plug,
  CheckCircle2,
  Cpu,
  FileCheck,
  TrendingUp,
  Database,
  Smartphone,
  QrCode,
  Calendar,
  Map,
  Heart,
  Recycle
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/Header';
import { TopBar } from '@/components/TopBar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/sections/ContactForm';
import { CustomersSection } from '@/components/sections/CustomersSection';
import { HreflangTags } from '@/components/HreflangTags';
import { useLanguage } from '@/i18n/LanguageContext';

// Images — premium architectural photography for B2B real estate
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&h=1080&fit=crop&q=85",
  energy: "https://images.unsplash.com/photo-1464938050520-ef2270bb8ce8?w=1200&h=800&fit=crop&q=85",
  air: "https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=1200&h=800&fit=crop&q=85",
  occupancy: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1200&h=800&fit=crop&q=85",
  lockers: "https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=1200&h=800&fit=crop&q=85",
  platform: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=800&fit=crop&q=85",
};

const RealEstatePage = () => {
  const { t } = useLanguage();
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // Solution cards data
  const solutionCards = [
    { 
      icon: Zap, 
      titleKey: 'realEstatePage.solEnergy', 
      descKey: 'realEstatePage.solEnergyDesc',
      color: 'bg-amber-500',
      link: '/building-energy-efficiency',
      img: 'https://customer-assets.emergentagent.com/job_a470fac2-fb0b-4aaa-ba3f-e2e91d857cf4/artifacts/2pq8rgsl_Energy_Monitoring.JPG'
    },
    { 
      icon: Droplets, 
      titleKey: 'realEstatePage.solLeak', 
      descKey: 'realEstatePage.solLeakDesc',
      color: 'bg-blue-500',
      link: '/smart-metering',
      img: 'https://customer-assets.emergentagent.com/job_a470fac2-fb0b-4aaa-ba3f-e2e91d857cf4/artifacts/4ea6t5mb_ceiling-concrete-crack-and-exposed-the-rusty-steel-2026-01-09-07-43-08-utc.jpg'
    },
    { 
      icon: Wind, 
      titleKey: 'realEstatePage.solAir', 
      descKey: 'realEstatePage.solAirDesc',
      color: 'bg-teal-500',
      link: '/indoor-air-quality-monitoring',
      img: 'https://customer-assets.emergentagent.com/job_a470fac2-fb0b-4aaa-ba3f-e2e91d857cf4/artifacts/jon8l5h8_the-air-conditioning-and-ventilation-system-2026-03-16-01-26-40-utc.jpg'
    },
    { 
      icon: Users, 
      titleKey: 'realEstatePage.solOccupancy', 
      descKey: 'realEstatePage.solOccupancyDesc',
      color: 'bg-purple-500',
      link: '/office-space-management',
      img: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&h=500&fit=crop&q=80'
    },
    { 
      icon: Sparkles, 
      titleKey: 'realEstatePage.solCleaning', 
      descKey: 'realEstatePage.solCleaningDesc',
      color: 'bg-pink-500',
      link: null,
      img: 'https://customer-assets.emergentagent.com/job_a470fac2-fb0b-4aaa-ba3f-e2e91d857cf4/artifacts/5gta7wkz_3d-rendering-luxury-public-male-toilet-2026-01-07-00-23-03-utc.jpg'
    },
    { 
      icon: Bell, 
      titleKey: 'realEstatePage.solSafety', 
      descKey: 'realEstatePage.solSafetyDesc',
      color: 'bg-red-500',
      link: null,
      img: 'https://customer-assets.emergentagent.com/job_a470fac2-fb0b-4aaa-ba3f-e2e91d857cf4/artifacts/4kxreull_woman-regulating-heating-temperature-with-a-modern-2026-03-17-20-15-39-utc.jpg'
    },
    { 
      icon: Box, 
      titleKey: 'realEstatePage.solLockers', 
      descKey: 'realEstatePage.solLockersDesc',
      color: 'bg-indigo-500',
      link: '/smart-lockers',
      img: 'https://customer-assets.emergentagent.com/job_a470fac2-fb0b-4aaa-ba3f-e2e91d857cf4/artifacts/f6v5mejl_Smart%20Locker%20Premium.png'
    },
    { 
      icon: Car, 
      titleKey: 'realEstatePage.solParking', 
      descKey: 'realEstatePage.solParkingDesc',
      color: 'bg-slate-600',
      link: null,
      img: 'https://images.unsplash.com/photo-1506521781263-d8422e82f27a?w=800&h=500&fit=crop&q=80',
      overlay: 'parking',
    },
    { 
      icon: MapPin, 
      titleKey: 'realEstatePage.solAsset', 
      descKey: 'realEstatePage.solAssetDesc',
      color: 'bg-orange-500',
      link: '/asset-tracking',
      img: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=800&h=500&fit=crop&q=80'
    },
    { 
      icon: Leaf, 
      titleKey: 'realEstatePage.solESG', 
      descKey: 'realEstatePage.solESGDesc',
      color: 'bg-green-600',
      link: null,
      img: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=800&h=500&fit=crop&q=80',
      badge: 'ISO 50001',
    },
  ];

  // Segment cards — with dedicated premium photos per segment
  const segmentCards = [
    { icon: Building2, titleKey: 'realEstatePage.segOffice', descKey: 'realEstatePage.segOfficeDesc', img: 'https://images.unsplash.com/photo-1515263487990-61b07816b324?w=800&h=500&fit=crop&q=80' },
    { icon: Hotel, titleKey: 'realEstatePage.segHotel', descKey: 'realEstatePage.segHotelDesc', img: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=500&fit=crop&q=80' },
    { icon: Home, titleKey: 'realEstatePage.segResidential', descKey: 'realEstatePage.segResidentialDesc', img: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&h=500&fit=crop&q=80' },
    { icon: ShoppingBag, titleKey: 'realEstatePage.segRetail', descKey: 'realEstatePage.segRetailDesc', img: 'https://images.unsplash.com/photo-1481437156560-3205f6a55735?w=800&h=500&fit=crop&q=80' },
    { icon: Landmark, titleKey: 'realEstatePage.segBanks', descKey: 'realEstatePage.segBanksDesc', img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=500&fit=crop&q=80' },
    { icon: Factory, titleKey: 'realEstatePage.segParks', descKey: 'realEstatePage.segParksDesc', img: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&h=500&fit=crop&q=80' },
    { icon: Briefcase, titleKey: 'realEstatePage.segDevelopers', descKey: 'realEstatePage.segDevelopersDesc', img: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&h=500&fit=crop&q=80' },
    { icon: BarChart3, titleKey: 'realEstatePage.segAssetManagers', descKey: 'realEstatePage.segAssetManagersDesc', img: 'https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=800&h=500&fit=crop&q=80' },
  ];

  // FAQ items
  const faqItems = [
    { qKey: 'realEstatePage.faq1Q', aKey: 'realEstatePage.faq1A' },
    { qKey: 'realEstatePage.faq2Q', aKey: 'realEstatePage.faq2A' },
    { qKey: 'realEstatePage.faq3Q', aKey: 'realEstatePage.faq3A' },
    { qKey: 'realEstatePage.faq4Q', aKey: 'realEstatePage.faq4A' },
    { qKey: 'realEstatePage.faq5Q', aKey: 'realEstatePage.faq5A' },
    { qKey: 'realEstatePage.faq6Q', aKey: 'realEstatePage.faq6A' },
    { qKey: 'realEstatePage.faq7Q', aKey: 'realEstatePage.faq7A' },
  ];

  // JSON-LD: FAQPage schema for rich snippets
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: t(item.qKey),
      acceptedAnswer: {
        '@type': 'Answer',
        text: t(item.aKey),
      },
    })),
  };

  // JSON-LD: Service schema for B2B SEO qualification
  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: t('realEstatePage.heroTitle'),
    provider: {
      '@type': 'Organization',
      name: '0g Baltics',
      url: 'https://0gbaltics.com',
      areaServed: ['Latvia', 'Lithuania', 'Estonia'],
    },
    description: t('realEstatePage.metaDesc'),
    areaServed: [
      { '@type': 'Country', name: 'Latvia' },
      { '@type': 'Country', name: 'Lithuania' },
      { '@type': 'Country', name: 'Estonia' },
    ],
    audience: {
      '@type': 'BusinessAudience',
      audienceType: 'Real estate developers, owners, operators and asset managers',
    },
    url: 'https://0gbaltics.com/real-estate',
  };

  // JSON-LD: BreadcrumbList for SERP navigation enhancement
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://0gbaltics.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: t('nav.solutionsMenu'),
        item: 'https://0gbaltics.com/#solutions',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: t('realEstatePage.heroBadge'),
        item: 'https://0gbaltics.com/real-estate',
      },
    ],
  };

  // Related solutions
  const relatedSolutions = [
    { 
      title: 'Pakuskapis.lv', 
      desc: t('realEstatePage.relatedPakuskapis'),
      url: 'https://pakuskapis.lv',
      external: true
    },
    { 
      title: t('realEstatePage.relatedSmartLockers'), 
      desc: t('realEstatePage.relatedSmartLockersDesc'),
      url: '/smart-lockers',
      external: false
    },
    { 
      title: t('realEstatePage.relatedIAQ'), 
      desc: t('realEstatePage.relatedIAQDesc'),
      url: '/indoor-air-quality-monitoring',
      external: false
    },
    { 
      title: t('realEstatePage.relatedEnergy'), 
      desc: t('realEstatePage.relatedEnergyDesc'),
      url: '/building-energy-efficiency',
      external: false
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t('realEstatePage.metaTitle')}</title>
        <meta name="description" content={t('realEstatePage.metaDesc')} />
        <meta name="keywords" content="IoT real estate, smart building solutions Baltics, building energy monitoring, leak detection buildings, indoor air quality monitoring, occupancy monitoring, smart lockers, parking occupancy, property technology Baltics, real estate digitalization, smart offices, smart hotels, ESG building data" />
        <link rel="canonical" href="https://0gbaltics.com/real-estate" />
        <meta property="og:title" content={t('realEstatePage.ogTitle')} />
        <meta property="og:description" content={t('realEstatePage.ogDesc')} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={IMAGES.hero} />
        <script type="application/ld+json">
          {JSON.stringify(faqJsonLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceJsonLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
      </Helmet>
      <HreflangTags />

      <TopBar />
      <Header />

      <main className="pt-20">
        
        {/* ========================================= */}
        {/* HERO SECTION */}
        {/* ========================================= */}
        <section className="relative py-20 md:py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden" data-testid="real-estate-hero">
          <div className="absolute inset-0 opacity-20">
            <img src={IMAGES.hero} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="container-0g relative z-10">
            <div className="max-w-3xl">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                {t('realEstatePage.heroBadge')}
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {t('realEstatePage.heroTitle')}
              </h1>
              <p className="text-xl text-slate-300 mb-4 leading-relaxed">
                {t('realEstatePage.heroSubtitle')}
              </p>
              <p className="text-lg text-slate-400 mb-8 leading-relaxed">
                {t('realEstatePage.heroDesc')}
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <Button 
                  size="lg"
                  className="bg-teal-500 hover:bg-teal-600 text-white"
                  onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                  data-testid="hero-cta-primary"
                >
                  {t('realEstatePage.ctaDemo')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-slate-500 text-white hover:bg-slate-800"
                  onClick={() => document.getElementById('solutions')?.scrollIntoView({ behavior: 'smooth' })}
                  data-testid="hero-cta-secondary"
                >
                  {t('realEstatePage.ctaExplore')}
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* CUSTOMERS / SOCIAL PROOF (logos marquee) */}
        {/* ========================================= */}
        <CustomersSection />

        {/* ========================================= */}
        {/* TRUST / POSITIONING STRIP */}
        {/* ========================================= */}
        <section className="py-8 bg-white border-b border-slate-200" data-testid="trust-strip">
          <div className="container-0g">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-8">
              {[
                { icon: Package, text: t('realEstatePage.trustReady') },
                { icon: Wifi, text: t('realEstatePage.trustMulti') },
                { icon: MapPin, text: t('realEstatePage.trustLocal') },
                { icon: Target, text: t('realEstatePage.trustScalable') },
                { icon: Settings, text: t('realEstatePage.trustIntegration') },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex items-center gap-2 justify-center text-center">
                    <Icon className="h-5 w-5 text-teal-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-slate-700">{item.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SOLUTIONS OVERVIEW */}
        {/* ========================================= */}
        <section id="solutions" className="py-20 md:py-32 bg-slate-50" data-testid="solutions-overview">
          <div className="container-0g">
            <div className="max-w-3xl mb-12">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('realEstatePage.solutionsBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('realEstatePage.solutionsTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('realEstatePage.solutionsDesc')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {solutionCards.map((card, i) => {
                const Icon = card.icon;
                const content = (
                  <div className="bg-white border border-slate-200 rounded-lg overflow-hidden h-full hover:border-teal-300 hover:shadow-lg transition-all group flex flex-col">
                    <div className="relative h-28 overflow-hidden bg-slate-100">
                      <img
                        src={card.img}
                        alt={t(card.titleKey)}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      {/* Parking occupancy SVG overlay */}
                      {card.overlay === 'parking' && (
                        <svg
                          viewBox="0 0 100 62"
                          preserveAspectRatio="none"
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          aria-hidden="true"
                        >
                          {/* Row 1 - mix of free (green) and occupied (red) */}
                          <rect x="8" y="12" width="10" height="14" fill="#22c55e" fillOpacity="0.55" stroke="#16a34a" strokeWidth="0.4" />
                          <rect x="20" y="12" width="10" height="14" fill="#ef4444" fillOpacity="0.55" stroke="#dc2626" strokeWidth="0.4" />
                          <rect x="32" y="12" width="10" height="14" fill="#ef4444" fillOpacity="0.55" stroke="#dc2626" strokeWidth="0.4" />
                          <rect x="44" y="12" width="10" height="14" fill="#22c55e" fillOpacity="0.55" stroke="#16a34a" strokeWidth="0.4" />
                          <rect x="56" y="12" width="10" height="14" fill="#ef4444" fillOpacity="0.55" stroke="#dc2626" strokeWidth="0.4" />
                          <rect x="68" y="12" width="10" height="14" fill="#22c55e" fillOpacity="0.55" stroke="#16a34a" strokeWidth="0.4" />
                          <rect x="80" y="12" width="10" height="14" fill="#ef4444" fillOpacity="0.55" stroke="#dc2626" strokeWidth="0.4" />
                          {/* Row 2 */}
                          <rect x="8" y="34" width="10" height="14" fill="#ef4444" fillOpacity="0.55" stroke="#dc2626" strokeWidth="0.4" />
                          <rect x="20" y="34" width="10" height="14" fill="#22c55e" fillOpacity="0.55" stroke="#16a34a" strokeWidth="0.4" />
                          <rect x="32" y="34" width="10" height="14" fill="#ef4444" fillOpacity="0.55" stroke="#dc2626" strokeWidth="0.4" />
                          <rect x="44" y="34" width="10" height="14" fill="#22c55e" fillOpacity="0.55" stroke="#16a34a" strokeWidth="0.4" />
                          <rect x="56" y="34" width="10" height="14" fill="#22c55e" fillOpacity="0.55" stroke="#16a34a" strokeWidth="0.4" />
                          <rect x="68" y="34" width="10" height="14" fill="#ef4444" fillOpacity="0.55" stroke="#dc2626" strokeWidth="0.4" />
                          <rect x="80" y="34" width="10" height="14" fill="#22c55e" fillOpacity="0.55" stroke="#16a34a" strokeWidth="0.4" />
                        </svg>
                      )}
                      {/* Icon */}
                      <div className={`absolute top-2.5 left-2.5 w-9 h-9 ${card.color} rounded-md flex items-center justify-center shadow-lg z-10`}>
                        <Icon className="h-4.5 w-4.5 text-white" strokeWidth={2.5} />
                      </div>
                      {/* Standard/certification badge (e.g. ISO 50001) */}
                      {card.badge && (
                        <div className="absolute top-2.5 right-2.5 z-10 inline-flex items-center px-2 py-1 rounded-sm text-[10px] font-bold tracking-wider uppercase bg-white/95 text-slate-900 border border-green-600 shadow-sm">
                          {card.badge}
                        </div>
                      )}
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-teal-600 transition-colors">
                        {t(card.titleKey)}
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed flex-1">
                        {t(card.descKey)}
                      </p>
                      {card.link && (
                        <span className="inline-flex items-center text-xs text-teal-600 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                          {t('realEstatePage.learnMore')} <ArrowRight className="h-3 w-3 ml-1" />
                        </span>
                      )}
                    </div>
                  </div>
                );
                
                return card.link ? (
                  <Link key={i} to={card.link}>{content}</Link>
                ) : (
                  <div key={i}>{content}</div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* DETAILED SOLUTIONS - ENERGY */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="detail-energy">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
                  <Zap className="h-4 w-4" />
                  {t('realEstatePage.detailEnergyBadge')}
                </div>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('realEstatePage.detailEnergyTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('realEstatePage.detailEnergyDesc')}
                </p>
                
                <div className="space-y-3 mb-6">
                  {[
                    t('realEstatePage.detailEnergyPoint1'),
                    t('realEstatePage.detailEnergyPoint2'),
                    t('realEstatePage.detailEnergyPoint3'),
                    t('realEstatePage.detailEnergyPoint4'),
                  ].map((point, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{point}</span>
                    </div>
                  ))}
                </div>
                
                <Link to="/building-energy-efficiency" className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700">
                  {t('realEstatePage.learnMore')} <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </div>
              
              <div className="flex flex-col gap-4">
                <div className="rounded-lg overflow-hidden border border-slate-200">
                  <img 
                    src={IMAGES.energy} 
                    alt={t('realEstatePage.detailEnergyTitle')}
                    loading="lazy"
                    className="w-full h-48 object-cover"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                {[
                  { stat: '10-25%', label: t('realEstatePage.statEnergySaving') },
                  { stat: '2-3', label: t('realEstatePage.statPayback') },
                  { stat: '24/7', label: t('realEstatePage.statMonitoring') },
                  { stat: 'Real-time', label: t('realEstatePage.statAlerts') },
                ].map((item, i) => (
                  <div key={i} className="bg-slate-50 border border-slate-200 p-6 rounded-lg text-center">
                    <div className="text-2xl font-bold text-teal-600 mb-1">{item.stat}</div>
                    <p className="text-sm text-slate-600">{item.label}</p>
                  </div>
                ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* DETAILED SOLUTIONS - LEAK DETECTION */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="detail-leak">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="order-2 lg:order-1">
                <div className="bg-slate-800 border border-slate-700 p-8 rounded-lg">
                  <AlertTriangle className="h-12 w-12 text-red-400 mb-6" />
                  <h3 className="text-xl font-semibold text-white mb-4">{t('realEstatePage.leakProblem')}</h3>
                  <p className="text-slate-300 mb-6">{t('realEstatePage.leakProblemDesc')}</p>
                  <div className="space-y-3">
                    {[
                      t('realEstatePage.leakRisk1'),
                      t('realEstatePage.leakRisk2'),
                      t('realEstatePage.leakRisk3'),
                    ].map((risk, i) => (
                      <div key={i} className="flex items-center gap-2 text-slate-400">
                        <span className="w-1.5 h-1.5 bg-red-400 rounded-full" />
                        {risk}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="order-1 lg:order-2">
                <div className="inline-flex items-center gap-2 bg-blue-900/50 text-blue-300 px-3 py-1 rounded-full text-sm font-medium mb-4">
                  <Droplets className="h-4 w-4" />
                  {t('realEstatePage.detailLeakBadge')}
                </div>
                <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                  {t('realEstatePage.detailLeakTitle')}
                </h2>
                <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                  {t('realEstatePage.detailLeakDesc')}
                </p>
                
                <div className="space-y-3 mb-6">
                  {[
                    t('realEstatePage.detailLeakPoint1'),
                    t('realEstatePage.detailLeakPoint2'),
                    t('realEstatePage.detailLeakPoint3'),
                  ].map((point, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-teal-400 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-300">{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* DETAILED SOLUTIONS - AIR QUALITY */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="detail-air">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
                  <Wind className="h-4 w-4" />
                  {t('realEstatePage.detailAirBadge')}
                </div>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('realEstatePage.detailAirTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('realEstatePage.detailAirDesc')}
                </p>
                
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {[
                    { icon: Thermometer, label: t('realEstatePage.airTemp') },
                    { icon: Droplets, label: t('realEstatePage.airHumidity') },
                    { icon: Wind, label: 'CO2' },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="bg-slate-50 p-4 rounded-lg text-center">
                        <Icon className="h-6 w-6 text-teal-600 mx-auto mb-2" />
                        <span className="text-sm text-slate-700 font-medium">{item.label}</span>
                      </div>
                    );
                  })}
                </div>
                
                <Link to="/indoor-air-quality-monitoring" className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700">
                  {t('realEstatePage.learnMore')} <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </div>
              
              <div className="bg-slate-50 p-8 rounded-lg">
                <div className="rounded-lg overflow-hidden border border-slate-200 mb-6">
                  <img 
                    src={IMAGES.air} 
                    alt={t('realEstatePage.detailAirTitle')}
                    loading="lazy"
                    className="w-full h-48 object-cover"
                  />
                </div>
                <h3 className="font-semibold text-slate-900 mb-4">{t('realEstatePage.airBenefits')}</h3>
                <div className="space-y-3">
                  {[
                    t('realEstatePage.airBenefit1'),
                    t('realEstatePage.airBenefit2'),
                    t('realEstatePage.airBenefit3'),
                    t('realEstatePage.airBenefit4'),
                  ].map((benefit, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* DETAILED SOLUTIONS - OCCUPANCY & LOCKERS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="detail-occupancy-lockers">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Occupancy */}
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden flex flex-col">
                <img 
                  src={IMAGES.occupancy} 
                  alt={t('realEstatePage.detailOccupancyTitle')}
                  loading="lazy"
                  className="w-full h-44 object-cover"
                />
                <div className="p-8 flex flex-col flex-1">
                <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium mb-4 w-fit">
                  <Users className="h-4 w-4" />
                  {t('realEstatePage.detailOccupancyBadge')}
                </div>
                <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                  {t('realEstatePage.detailOccupancyTitle')}
                </h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {t('realEstatePage.detailOccupancyDesc')}
                </p>
                <div className="space-y-2 mb-6">
                  {[
                    t('realEstatePage.occupancyUse1'),
                    t('realEstatePage.occupancyUse2'),
                    t('realEstatePage.occupancyUse3'),
                    t('realEstatePage.occupancyUse4'),
                  ].map((use, i) => (
                    <div key={i} className="flex items-center gap-2 text-slate-700">
                      <CheckCircle className="h-4 w-4 text-purple-500" />
                      {use}
                    </div>
                  ))}
                </div>
                <Link to="/office-space-management" className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700">
                  {t('realEstatePage.learnMore')} <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
                </div>
              </div>

              {/* Smart Lockers */}
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden flex flex-col">
                <img 
                  src={IMAGES.lockers} 
                  alt={t('realEstatePage.detailLockersTitle')}
                  loading="lazy"
                  className="w-full h-44 object-cover"
                />
                <div className="p-8 flex flex-col flex-1">
                <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-medium mb-4 w-fit">
                  <Box className="h-4 w-4" />
                  {t('realEstatePage.detailLockersBadge')}
                </div>
                <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                  {t('realEstatePage.detailLockersTitle')}
                </h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {t('realEstatePage.detailLockersDesc')}
                </p>
                <div className="space-y-2 mb-6">
                  {[
                    t('realEstatePage.lockerUse1'),
                    t('realEstatePage.lockerUse2'),
                    t('realEstatePage.lockerUse3'),
                    t('realEstatePage.lockerUse4'),
                  ].map((use, i) => (
                    <div key={i} className="flex items-center gap-2 text-slate-700">
                      <CheckCircle className="h-4 w-4 text-indigo-500" />
                      {use}
                    </div>
                  ))}
                </div>
                <Link to="/smart-lockers" className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700">
                  {t('realEstatePage.learnMore')} <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* USE CASES BY SEGMENT */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="segments">
          <div className="container-0g">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('realEstatePage.segmentsBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('realEstatePage.segmentsTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('realEstatePage.segmentsDesc')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {segmentCards.map((card, i) => {
                const Icon = card.icon;
                return (
                  <div key={i} className="group bg-white border border-slate-200 rounded-lg overflow-hidden hover:border-teal-300 hover:shadow-lg transition-all">
                    <div className="relative h-40 overflow-hidden bg-slate-100">
                      <img
                        src={card.img}
                        alt={t(card.titleKey)}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3 w-10 h-10 rounded-md bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-md">
                        <Icon className="h-5 w-5 text-teal-600" />
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-semibold text-slate-900 mb-2">{t(card.titleKey)}</h3>
                      <p className="text-sm text-slate-600">{t(card.descKey)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* CONNECTIVITY & PLATFORM */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="platform">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                  {t('realEstatePage.platformBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                  {t('realEstatePage.platformTitle')}
                </h2>
                <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                  {t('realEstatePage.platformDesc')}
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Gauge, label: t('realEstatePage.platformDevices') },
                    { icon: Wifi, label: t('realEstatePage.platformConnectivity') },
                    { icon: BarChart3, label: t('realEstatePage.platformDashboard') },
                    { icon: Bell, label: t('realEstatePage.platformAlerts') },
                    { icon: Settings, label: t('realEstatePage.platformIntegration') },
                    { icon: Shield, label: t('realEstatePage.platformSupport') },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-center gap-3 bg-slate-800 border border-slate-700 p-4 rounded-lg">
                        <Icon className="h-5 w-5 text-teal-400" />
                        <span className="text-slate-300 text-sm">{item.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="bg-slate-800 border border-slate-700 p-8 rounded-lg">
                <div className="rounded-lg overflow-hidden border border-slate-700 mb-6">
                  <img 
                    src={IMAGES.platform} 
                    alt={t('realEstatePage.platformTitle')}
                    loading="lazy"
                    className="w-full h-44 object-cover"
                  />
                </div>
                <h3 className="text-xl font-semibold text-white mb-6">{t('realEstatePage.techTitle')}</h3>
                <div className="space-y-4">
                  {[
                    { name: 'LoRaWAN', desc: t('realEstatePage.techLoRaWAN') },
                    { name: 'NB-IoT', desc: t('realEstatePage.techNBIoT') },
                    { name: 'BLE', desc: t('realEstatePage.techBLE') },
                  ].map((tech, i) => (
                    <div key={i} className="bg-slate-700 p-4 rounded-lg">
                      <h4 className="text-white font-medium mb-1">{tech.name}</h4>
                      <p className="text-slate-400 text-sm">{tech.desc}</p>
                    </div>
                  ))}
                </div>
                <Link to="/connectivity" className="inline-flex items-center text-teal-400 font-semibold mt-6 hover:text-teal-300">
                  {t('realEstatePage.learnMoreConnectivity')} <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* BMS / BACnet INTEGRATION (TEKTELIC-powered) */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white border-t border-slate-200" data-testid="bms-integration">
          <div className="container-0g">
            <div className="max-w-3xl mb-12">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('realEstatePage.bmsBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('realEstatePage.bmsTitle')}
              </h2>
              <p className="text-lg text-slate-600">
                {t('realEstatePage.bmsDesc')}
              </p>
            </div>

            {/* Protocols grid */}
            <div className="mb-10">
              <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-6">
                {t('realEstatePage.bmsProtocols')}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { icon: Network, titleKey: 'realEstatePage.bmsBACnet', descKey: 'realEstatePage.bmsBACnetDesc', accent: 'bg-teal-500' },
                  { icon: Plug, titleKey: 'realEstatePage.bmsModbus', descKey: 'realEstatePage.bmsModbusDesc', accent: 'bg-blue-500' },
                  { icon: Server, titleKey: 'realEstatePage.bmsRest', descKey: 'realEstatePage.bmsRestDesc', accent: 'bg-indigo-500' },
                  { icon: Wifi, titleKey: 'realEstatePage.bmsLoRaWAN', descKey: 'realEstatePage.bmsLoRaWANDesc', accent: 'bg-amber-500' },
                ].map((p, i) => {
                  const Icon = p.icon;
                  return (
                    <div key={i} className="relative bg-slate-50 border border-slate-200 rounded-lg p-5 hover:border-teal-400 hover:shadow-md transition-all">
                      <div className={`absolute -top-3 left-5 w-10 h-10 rounded-md ${p.accent} flex items-center justify-center shadow-lg`}>
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <div className="pt-4">
                        <h4 className="font-semibold text-slate-900 mb-1.5">{t(p.titleKey)}</h4>
                        <p className="text-sm text-slate-600 leading-relaxed">{t(p.descKey)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Device ecosystem visual — full TEKTELIC portfolio in a building */}
            <div className="mb-10 rounded-lg overflow-hidden border border-slate-200 bg-slate-50 p-4 md:p-6 shadow-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_a470fac2-fb0b-4aaa-ba3f-e2e91d857cf4/artifacts/1ubb8agy_image.png"
                alt={t('realEstatePage.bmsEcosystemTitle')}
                loading="lazy"
                className="w-full h-auto object-contain rounded-md"
              />
            </div>

            {/* Ecosystem + It just works */}
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white rounded-lg p-8 border border-teal-500/20">
                <div className="w-12 h-12 rounded-md bg-teal-500/10 border border-teal-400/30 flex items-center justify-center mb-4">
                  <Cpu className="h-6 w-6 text-teal-300" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{t('realEstatePage.bmsEcosystemTitle')}</h3>
                <p className="text-slate-300 leading-relaxed">{t('realEstatePage.bmsEcosystemDesc')}</p>
              </div>
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-8">
                <div className="w-12 h-12 rounded-md bg-teal-500 flex items-center justify-center mb-4">
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{t('realEstatePage.bmsItWorks')}</h3>
                <p className="text-slate-700 leading-relaxed">{t('realEstatePage.bmsItWorksDesc')}</p>
              </div>
            </div>

            {/* Benefit bullets */}
            <div className="grid md:grid-cols-4 gap-3">
              {['bmsBenefit1', 'bmsBenefit2', 'bmsBenefit3', 'bmsBenefit4'].map((k) => (
                <div key={k} className="flex items-start gap-3 bg-white border border-slate-200 rounded-md p-4">
                  <CheckCircle2 className="h-5 w-5 text-teal-600 shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-slate-900">{t(`realEstatePage.${k}`)}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* FACILITY MANAGEMENT IoT SUITE (TEKTELIC-inspired, SEO-rich) */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-50 border-t border-slate-200" data-testid="facility-management">
          <div className="container-0g">
            {/* Header */}
            <div className="max-w-4xl mb-14">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('realEstatePage.fmBadge')}
              </span>
              <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 mb-6 leading-tight">
                {t('realEstatePage.fmTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('realEstatePage.fmDesc')}
              </p>
            </div>

            {/* Stats row */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
              {[
                { stat: '21%', key: 'fmStat1' },
                { stat: '30%', key: 'fmStat2' },
                { stat: '100%', key: 'fmStat3' },
                { stat: '24/7', key: 'fmStat4' },
              ].map((item, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-lg p-6 hover:border-teal-400 hover:shadow-md transition-all">
                  <div className="text-4xl font-bold text-teal-600 mb-2">{item.stat}</div>
                  <p className="text-sm text-slate-700 leading-snug">{t(`realEstatePage.${item.key}`)}</p>
                </div>
              ))}
            </div>

            {/* Architecture visual — sensors → gateway → cloud → dashboard */}
            <div className="mb-16 rounded-lg overflow-hidden border border-slate-200 bg-white p-4 md:p-8 shadow-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_a470fac2-fb0b-4aaa-ba3f-e2e91d857cf4/artifacts/y3g123e5_image.png"
                alt={t('realEstatePage.fmTitle')}
                loading="lazy"
                className="w-full h-auto object-contain rounded-md mx-auto max-w-4xl"
              />
            </div>

            {/* Challenges */}
            <div className="mb-16">
              <h3 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
                {t('realEstatePage.fmChallengesTitle')}
              </h3>
              <p className="text-slate-600 mb-8 max-w-3xl">
                {t('realEstatePage.fmChallengesDesc')}
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { icon: Thermometer, t: 'fmCh1Title', d: 'fmCh1Desc', color: 'text-orange-600 bg-orange-50' },
                  { icon: AlertTriangle, t: 'fmCh2Title', d: 'fmCh2Desc', color: 'text-red-600 bg-red-50' },
                  { icon: FileCheck, t: 'fmCh3Title', d: 'fmCh3Desc', color: 'text-green-600 bg-green-50' },
                  { icon: Clock, t: 'fmCh4Title', d: 'fmCh4Desc', color: 'text-amber-600 bg-amber-50' },
                  { icon: TrendingUp, t: 'fmCh5Title', d: 'fmCh5Desc', color: 'text-blue-600 bg-blue-50' },
                  { icon: Database, t: 'fmCh6Title', d: 'fmCh6Desc', color: 'text-indigo-600 bg-indigo-50' },
                ].map((c, i) => {
                  const Ic = c.icon;
                  return (
                    <div key={i} className="bg-white border border-slate-200 rounded-lg p-6 hover:border-teal-300 hover:shadow-md transition-all">
                      <div className={`w-11 h-11 rounded-md ${c.color} flex items-center justify-center mb-4`}>
                        <Ic className="h-5 w-5" strokeWidth={2.2} />
                      </div>
                      <h4 className="font-semibold text-slate-900 mb-2">{t(`realEstatePage.${c.t}`)}</h4>
                      <p className="text-sm text-slate-600 leading-relaxed">{t(`realEstatePage.${c.d}`)}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Ready-to-use applications */}
            <div className="mb-16">
              <h3 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
                {t('realEstatePage.fmAppsTitle')}
              </h3>
              <p className="text-slate-600 mb-8 max-w-3xl">
                {t('realEstatePage.fmAppsDesc')}
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                {[
                  { 
                    name: 'fmApp1Name', desc: 'fmApp1Desc', 
                    gradient: 'from-slate-900 via-slate-800 to-teal-950',
                    img: 'https://customer-assets.emergentagent.com/job_a470fac2-fb0b-4aaa-ba3f-e2e91d857cf4/artifacts/6ykaifru_image.png',
                  },
                  { 
                    name: 'fmApp2Name', desc: 'fmApp2Desc', 
                    gradient: 'from-slate-900 via-slate-800 to-indigo-950',
                    img: 'https://customer-assets.emergentagent.com/job_a470fac2-fb0b-4aaa-ba3f-e2e91d857cf4/artifacts/h1flprdg_image.png',
                  },
                  { 
                    name: 'fmApp3Name', desc: 'fmApp3Desc', 
                    gradient: 'from-slate-900 via-slate-800 to-purple-950',
                    img: 'https://customer-assets.emergentagent.com/job_a470fac2-fb0b-4aaa-ba3f-e2e91d857cf4/artifacts/dgiv93gd_image.png',
                  },
                  { 
                    name: 'fmApp4Name', desc: 'fmApp4Desc', 
                    gradient: 'from-slate-900 via-slate-800 to-amber-950',
                    img: 'https://customer-assets.emergentagent.com/job_a470fac2-fb0b-4aaa-ba3f-e2e91d857cf4/artifacts/ou8ytimg_image.png',
                  },
                ].map((a, i) => (
                  <div key={i} className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:border-teal-400 hover:shadow-xl transition-all group">
                    <div className={`relative h-44 bg-gradient-to-br ${a.gradient} flex items-center justify-center overflow-hidden`}>
                      <img
                        src={a.img}
                        alt={t(`realEstatePage.${a.name}`)}
                        loading="lazy"
                        className="relative h-full w-full object-contain p-3 group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <h4 className="text-lg font-bold text-slate-900 mb-2 tracking-wide">
                        {t(`realEstatePage.${a.name}`)}
                      </h4>
                      <p className="text-sm text-slate-600 leading-relaxed">{t(`realEstatePage.${a.desc}`)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Wireless BMS Integration block with architecture diagram */}
            <div className="mb-16 grid lg:grid-cols-2 gap-8 items-center bg-white border border-slate-200 rounded-lg p-6 md:p-10 shadow-sm">
              <div>
                <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  <Wifi className="h-3.5 w-3.5" />
                  BACnet · Modbus · Niagara
                </span>
                <h3 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-5 leading-tight">
                  {t('realEstatePage.fmBmsTitle')}
                </h3>
                <p className="text-slate-700 leading-relaxed mb-4">
                  {t('realEstatePage.fmBmsDesc1')}
                </p>
                <p className="text-slate-600 leading-relaxed">
                  {t('realEstatePage.fmBmsDesc2')}
                </p>
              </div>
              <div className="bg-slate-50 rounded-md p-4 md:p-6 border border-slate-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_a470fac2-fb0b-4aaa-ba3f-e2e91d857cf4/artifacts/hqpqlm1f_image.png"
                  alt={t('realEstatePage.fmBmsTitle')}
                  loading="lazy"
                  className="w-full h-auto object-contain rounded"
                />
              </div>
            </div>

            {/* Benefits */}
            <div>
              <h3 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-8 max-w-3xl">
                {t('realEstatePage.fmBenefitsTitle')}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { icon: TrendingUp, t: 'fmBen1Title', d: 'fmBen1Desc' },
                  { icon: Zap, t: 'fmBen2Title', d: 'fmBen2Desc' },
                  { icon: Heart, t: 'fmBen3Title', d: 'fmBen3Desc' },
                  { icon: FileCheck, t: 'fmBen4Title', d: 'fmBen4Desc' },
                  { icon: Shield, t: 'fmBen5Title', d: 'fmBen5Desc' },
                  { icon: Recycle, t: 'fmBen6Title', d: 'fmBen6Desc' },
                ].map((b, i) => {
                  const Ic = b.icon;
                  return (
                    <div key={i} className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-lg p-6 border border-teal-500/20 hover:border-teal-400/60 transition-all">
                      <div className="w-11 h-11 rounded-md bg-teal-500/10 border border-teal-400/30 flex items-center justify-center mb-4">
                        <Ic className="h-5 w-5 text-teal-300" strokeWidth={2.2} />
                      </div>
                      <h4 className="font-semibold text-white mb-2">{t(`realEstatePage.${b.t}`)}</h4>
                      <p className="text-sm text-slate-300 leading-relaxed">{t(`realEstatePage.${b.d}`)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* RELATED SOLUTIONS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="related-solutions">
          <div className="container-0g">
            <div className="max-w-3xl mb-12">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('realEstatePage.relatedBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('realEstatePage.relatedTitle')}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedSolutions.map((item, i) => {
                const content = (
                  <div className="bg-white border border-slate-200 rounded-lg p-6 h-full hover:border-teal-300 hover:shadow-md transition-all group">
                    <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-teal-600 transition-colors flex items-center gap-2">
                      {item.title}
                      {item.external && <ExternalLink className="h-4 w-4 text-slate-400" />}
                    </h3>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                );
                
                return item.external ? (
                  <a key={i} href={item.url} target="_blank" rel="noopener noreferrer">{content}</a>
                ) : (
                  <Link key={i} to={item.url}>{content}</Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* FAQ SECTION */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="faq-section">
          <div className="container-0g">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('realEstatePage.faqBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('realEstatePage.faqTitle')}
                </h2>
              </div>

              <div className="space-y-4">
                {faqItems.map((item, i) => (
                  <div key={i} className="border border-slate-200 rounded-lg overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-slate-50 transition-colors"
                      onClick={() => toggleFaq(i)}
                      data-testid={`faq-toggle-${i}`}
                    >
                      <span className="font-semibold text-slate-900 pr-4">{t(item.qKey)}</span>
                      {openFaq === i ? (
                        <ChevronUp className="h-5 w-5 text-teal-600 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />
                      )}
                    </button>
                    {openFaq === i && (
                      <div className="px-6 pb-6 text-slate-600 leading-relaxed">
                        {t(item.aKey)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* CTA SECTION */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-teal-600" data-testid="cta-section">
          <div className="container-0g text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('realEstatePage.ctaTitle')}
            </h2>
            <p className="text-xl text-teal-100 mb-8 max-w-3xl mx-auto">
              {t('realEstatePage.ctaDesc')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg"
                className="bg-white text-teal-700 hover:bg-teal-50 font-semibold"
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="cta-button-demo"
              >
                {t('realEstatePage.ctaDemo')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-teal-500"
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="cta-button-pilot"
              >
                {t('realEstatePage.ctaPilot')}
              </Button>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* CONTACT FORM */}
        {/* ========================================= */}
        <div id="contact-form">
          <ContactForm />
        </div>

      </main>

      <Footer />
    </>
  );
};

export default RealEstatePage;
